/**
 * The map's presentation, resolved (D169 the style and tint; D189 the
 * title; D209 the styles offered) — the pack's optional `presentation.map`
 * block filled from the built-in defaults, so every reader sees one complete
 * shape.
 *
 * @remarks
 * Lives in the engine rather than the loader because the engine reads it
 * too: the transcript render of `MAP` (`renderMapText`) heads itself with
 * the map's title, and the engine bundle carries the pack's manifest as
 * authored, not the loader's resolved copy. One resolution, two callers —
 * the loader's `resolvePresentation` composes this into the shell's whole
 * presentation, and the map handler asks it directly. Pure and total: a
 * missing block resolves to the defaults, never a throw.
 *
 * The defaults are deliberately fiction-free (Mike, 2026-09-10 for the
 * hologram; D189 for the word): "Map" is what a pack gets for saying nothing,
 * because "Facility map" was the POC's facility, and nothing a pack did not
 * write should sound like a fiction it does not have.
 *
 * D209: `styles` is what the player may choose among and `style` the map
 * the session opens on. A pack that lists styles and names no opening opens
 * on the first it listed; a pack that lists nothing offers exactly its
 * opening style — one map, no chooser. The schema has already refused an
 * opening style outside the list and a style listed twice, so the resolved
 * shape always has `style` among `styles`, each once.
 *
 * @packageDocumentation
 */

import type { MapPresentation, MapStyle } from '@orpheus/content-schema';

/** How the map is drawn and named, fully resolved: the opening style, the styles offered, and the title always known; the tint only when the pack chose one. */
export interface ResolvedMapPresentation {
	/** The map the session opens on — always one of {@link ResolvedMapPresentation.styles}. */
	readonly style: MapStyle;
	/** D209: the styles the player may choose among, in chooser order. One entry is a lock. */
	readonly styles: readonly MapStyle[];
	readonly tint?: string;
	/** What the map is called — the panel heading, the figure's name, the transcript render's header. */
	readonly title: string;
}

/**
 * What a pack gets for saying nothing about the map: the scene — the rooms
 * in three dimensions, drawn as the hologram where the browser cannot draw
 * them (D215, Mike 2026-09-27; until then the hologram itself, Mike
 * 2026-09-10). In the brand's green either way.
 */
export const DEFAULT_MAP_STYLE: MapStyle = 'scene';

/** What a pack's map is called when the pack does not say (D189). */
export const DEFAULT_MAP_TITLE = 'Map';

/**
 * Resolve the map's presentation: the opening style from the declaration,
 * else the first offered, else the default; the offered styles from the
 * declaration, else the opening style alone; the title from the default;
 * the tint as declared.
 *
 * @param declared - The pack's `presentation.map` block, if any
 * @returns The complete map presentation
 */
export const resolveMapPresentation = (declared: MapPresentation | undefined): ResolvedMapPresentation => {
	const style = declared?.style ?? declared?.styles?.[0] ?? DEFAULT_MAP_STYLE;

	return {
		style,
		styles: declared?.styles ?? [style],
		...(declared?.tint === undefined ? {} : { tint: declared.tint }),
		title: declared?.title ?? DEFAULT_MAP_TITLE,
	};
};
